import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheck, FiArrowRight, FiInfo, FiLayers, FiAlertCircle, FiDatabase, FiCpu, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { DEMO_WORKFLOW, GUEST_DATA, FAQ_DATA } from '../data/mockData';

export default function DemoCenter() {
  const [currentStep, setCurrentStep] = useState(2);
  const [appState, setAppState] = useState('success');
  const [filterTier, setFilterTier] = useState('All');
  const [openFaq, setOpenFaq] = useState(null);

  const filteredGuests = GUEST_DATA.filter(g => filterTier === 'All' || g.tier === filterTier);

  const getStepStatus = (id) => {
    if (id < currentStep) return 'completed';
    if (id === currentStep) return 'active';
    return 'pending';
  };

  return (
    <div className="h-full space-y-8 overflow-y-auto pb-24 pr-2">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-white flex items-center gap-3">
            <SafeIcon icon={FiCpu} className="text-[#9D4EDD]" />
            How It Works
          </h1>
          <p className="text-[#94A3B8] text-sm md:text-base">Experience the end-to-end EventNova execution loop.</p>
        </div>
        
        <div className="flex items-center gap-2 bg-black/40 p-1.5 rounded-xl border border-white/10">
          <span className="text-[10px] text-[#94A3B8] uppercase tracking-widest px-2 font-bold hidden lg:block">Override State:</span>
          {['success', 'loading', 'empty', 'warning'].map(state => (
            <button
              key={state}
              onClick={() => setAppState(state)}
              className={`px-3 py-1.5 rounded-lg text-[10px] uppercase font-bold tracking-widest transition-all ${
                appState === state ? 'bg-[#9D4EDD] text-white shadow-[0_0_10px_rgba(157,78,221,0.5)]' : 'text-[#94A3B8] hover:text-white'
              }`}
            >
              {state}
            </button>
          ))}
        </div>
      </div>

      {/* Main Demo Section */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Try This Flow Stepper */}
        <div className="xl:col-span-1 space-y-3">
          <div className="text-xs font-bold text-[#E5C158] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
            <div className="w-1 h-4 bg-[#E5C158]"></div>
            Try This Flow
          </div>
          <div className="space-y-2">
            {DEMO_WORKFLOW.map((step) => {
              const status = getStepStatus(step.id);
              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(step.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all duration-300 relative group overflow-hidden ${
                    status === 'active' ? 'bg-[#9D4EDD]/10 border-[#9D4EDD] shadow-[0_0_15px_rgba(157,78,221,0.1)]' : 
                    status === 'completed' ? 'bg-white/5 border-green-500/30' : 'bg-[#1A1A24]/50 border-white/5 opacity-40'
                  }`}
                >
                  <div className="flex gap-3 items-start relative z-10">
                    <div className={`mt-1 shrink-0 ${status === 'active' ? 'text-[#E5C158]' : status === 'completed' ? 'text-green-400' : 'text-[#94A3B8]'}`}>
                      <SafeIcon icon={step.icon} className="text-lg" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white flex items-center gap-2">
                         {step.title}
                         {status === 'completed' && <SafeIcon icon={FiCheck} className="text-green-400 text-xs" />}
                      </div>
                      <p className="text-[11px] text-[#94A3B8] mt-1 leading-relaxed line-clamp-2">{step.description}</p>
                    </div>
                  </div>
                  {status === 'active' && <div className="absolute right-3 top-4 text-[#E5C158] animate-pulse"><SafeIcon icon={FiArrowRight} /></div>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Interactive Sandbox */}
        <div className="xl:col-span-3 glass-panel flex flex-col min-h-[500px] overflow-hidden">
          <div className="p-6 border-b border-white/10 bg-black/40 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <div className="bg-[#E5C158]/10 p-2.5 rounded-xl text-[#E5C158] border border-[#E5C158]/20">
                <SafeIcon icon={FiDatabase} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Sample Data Engine</h2>
                <p className="text-xs text-[#94A3B8]">Execution Layer: Phase {currentStep}</p>
              </div>
            </div>
            
            {currentStep === 2 && (
              <div className="flex gap-1 bg-black/40 p-1 rounded-lg border border-white/5 w-full sm:w-auto overflow-x-auto">
                {['All', 'VIP', 'General', 'Press'].map(tier => (
                  <button
                    key={tier}
                    onClick={() => setFilterTier(tier)}
                    className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase transition-all whitespace-nowrap ${
                      filterTier === tier ? 'bg-white/10 text-[#E5C158] border border-[#E5C158]/20' : 'text-[#94A3B8]'
                    }`}
                  >
                    {tier}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 p-6 overflow-y-auto">
            <AnimatePresence mode="wait">
              {appState === 'loading' ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-12 h-12 border-4 border-[#9D4EDD]/20 border-t-[#9D4EDD] rounded-full animate-spin"></div>
                  <p className="text-[#94A3B8] font-mono text-sm tracking-widest uppercase">Synchronizing Cosmic Nodes...</p>
                </motion.div>
              ) : appState === 'empty' ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center text-white/20 border border-white/10">
                    <SafeIcon icon={FiLayers} className="text-4xl" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">Registry Vacant</h3>
                    <p className="text-sm text-[#94A3B8] max-w-xs mt-2 mx-auto">Upload your guest matrix or link a vendor node to populate this simulation area.</p>
                  </div>
                </motion.div>
              ) : appState === 'warning' ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-red-500/5 border border-red-500/20 rounded-2xl p-10 flex flex-col items-center text-center space-y-6">
                  <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center text-red-500 animate-pulse">
                    <SafeIcon icon={FiAlertCircle} className="text-4xl" />
                  </div>
                  <div>
                    <h3 className="text-red-400 font-bold text-xl">Operational Conflict</h3>
                    <p className="text-sm text-[#94A3B8] max-w-md mt-2 mx-auto">Critical vendor node mismatch detected in 'Sector 4'. Please manually verify comm-link integrity before proceeding.</p>
                  </div>
                  <button className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl text-sm font-black shadow-[0_0_20px_rgba(239,68,68,0.3)] transition-all">
                    Resolve Node Conflict
                  </button>
                </motion.div>
              ) : (
                <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                  {currentStep === 2 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredGuests.map((guest, i) => (
                        <div key={guest.id} className="bg-white/5 border border-white/5 rounded-xl p-4 hover:border-[#9D4EDD]/40 transition-all cursor-pointer group">
                          <div className="flex justify-between items-start mb-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#9D4EDD] to-indigo-600 flex items-center justify-center text-sm font-black text-white shadow-lg">
                              {guest.name.charAt(0)}
                            </div>
                            <span className={`text-[10px] px-2 py-1 rounded-lg border font-bold ${
                              guest.tier === 'VIP' || guest.tier === 'VVIP' ? 'text-[#E5C158] border-[#E5C158]/30 bg-[#E5C158]/5' : 'text-[#94A3B8] border-white/10 bg-white/5'
                            }`}>
                              {guest.tier}
                            </span>
                          </div>
                          <h4 className="text-white font-bold group-hover:text-[#E5C158] transition-colors">{guest.name}</h4>
                          <div className="flex items-center gap-4 mt-3">
                            <div className="text-[10px] text-[#94A3B8] uppercase">Table: <span className="text-white font-mono">{guest.table}</span></div>
                            <div className="text-[10px] text-[#94A3B8] uppercase">Energy: <span className="text-green-400 font-mono">{guest.energy}%</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-[#E5C158]/10 rounded-full flex items-center justify-center text-[#E5C158] mb-6 border border-[#E5C158]/20">
                        <SafeIcon icon={FiInfo} className="text-3xl" />
                      </div>
                      <h3 className="text-2xl font-black text-white">{DEMO_WORKFLOW[currentStep-1].title}</h3>
                      <p className="text-[#94A3B8] mt-3 max-w-md mx-auto leading-relaxed">
                        This module demonstrates the system architecture during the {DEMO_WORKFLOW[currentStep-1].title.toLowerCase()} phase. In production, this panel dynamically recalibrates based on real-time event telemetry.
                      </p>
                      <button 
                        onClick={() => setCurrentStep(prev => prev < 6 ? prev + 1 : 1)}
                        className="mt-10 bg-[#9D4EDD] hover:bg-[#8a2be2] text-white px-10 py-4 rounded-2xl font-black shadow-xl flex items-center gap-3 transition-all transform hover:scale-105"
                      >
                        Proceed to Next Phase
                        <SafeIcon icon={FiArrowRight} />
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="glass-panel p-8">
        <h2 className="text-2xl font-black text-white mb-8 border-b border-white/10 pb-4">Studio Intelligence FAQ</h2>
        <div className="space-y-4">
          {FAQ_DATA.map((faq, idx) => (
            <div key={idx} className="border border-white/5 rounded-2xl overflow-hidden bg-white/20">
              <button 
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                className="w-full flex justify-between items-center p-5 text-left hover:bg-white/5 transition-colors"
              >
                <span className="font-bold text-white pr-4">{faq.q}</span>
                <SafeIcon icon={openFaq === idx ? FiChevronUp : FiChevronDown} className="text-[#9D4EDD] shrink-0" />
              </button>
              <AnimatePresence>
                {openFaq === idx && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                    <div className="px-5 pb-5 text-sm text-[#94A3B8] leading-relaxed border-t border-white/5 pt-4 bg-black/20">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center pt-8">
        <p className="text-sm text-[#94A3B8] font-light">
          Made by LingLink AI and <span className="text-[#E5C158] font-bold">DigLab AI</span>
        </p>
      </div>
    </div>
  );
}